import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import postOperatorReviewsSearch from 'api/facilities/post-operator-reviews-search';
import {type VerbatimReviewsResults} from 'api/facilities/verbatim-reviews.types';
import {
    type Filters,
    type VerbatimReviewsState,
} from 'pages/facilities/driver-feedback/verbatim-reviews/VerbatimReviews.types';
import {type FilterDrawerFilters} from 'pages/facilities/driver-feedback/verbatim-reviews/filters/utils';

interface PostOperatorReviewsSearchParams {
    facilityId: number;
    filters: Filters;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}

interface PostOperatorReviewsSearchThunk {
    state: {
        driverFeedback: {
            verbatimReviews: VerbatimReviewsState;
        };
    };
    rejectValue: unknown;
    signal: AbortSignal;
}

/**
 * Fetches the verbatim reviews data for a given facility.
 *
 * This action creator makes an asynchronous call to the API to retrieve
 * the verbatim reviews data for the specified facility.
 *
 * Action Type: 'facilities/postOperatorReviewsSearchData'
 *
 * API Service: {@link postOperatorReviewsSearch}
 */
export const postOperatorReviewsSearchData = createAsyncThunk<
    VerbatimReviewsResults,
    PostOperatorReviewsSearchParams,
    PostOperatorReviewsSearchThunk
>(
    'facilities/postOperatorReviewsSearchData',
    async (
        {facilityId, filters, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response: VerbatimReviewsResults =
                await postOperatorReviewsSearch(facilityId, filters, {
                    signal: abortSignal,
                });

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Fetches the verbatim reviews paginated data for a given facility.
 *
 * This action creator makes an asynchronous call to the API to retrieve
 * the verbatim reviews data for the specified facility.
 *
 * Action Type: 'facilities/postOperatorReviewsSearchPaginatedData'
 *
 * API Service: {@link postOperatorReviewsSearchPaginatedData}
 */
export const postOperatorReviewsSearchPaginatedData = createAsyncThunk<
    VerbatimReviewsResults,
    PostOperatorReviewsSearchParams
>(
    'facilities/postOperatorReviewsSearchPaginatedData',
    async (
        {facilityId, filters, handleAbortSignal, abortSignal},
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response: VerbatimReviewsResults =
                await postOperatorReviewsSearch(facilityId, filters, {
                    signal: abortSignal,
                });

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Action to handle the state of the search text
 */
export const setVerbatimReviewsSearchText = createAction<string>(
    'facilities/setVerbatimReviewsSearchText'
);

/**
 * Action to handle the state of the filter drawer
 */
export const setVerbatimReviewsFilters = createAction<FilterDrawerFilters>(
    'facilities/setVerbatimReviewsFilters'
);

/**
 * Action to handle the state of the 'sort by' selection
 */
export const setVerbatimReviewsSort = createAction<string>(
    'facilities/setVerbatimReviewsSort'
);
